import React, { useMemo, useState } from 'react';
import { Form, Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface DistrictSelectProps {
  disabled?: boolean;
  placeholder?: string;
  form: any;
  regionFieldName?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * District select component with optional region filtering.
 * Fetches districts from the 'district' provider.
 * When a region is selected, filters districts by that region.
 *
 * Used for parameter type: DISTRICT_SELECT
 */
const DistrictSelect: React.FC<DistrictSelectProps> = ({
  disabled,
  placeholder,
  form,
  regionFieldName = 'region',
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const regionValue = Form.useWatch(regionFieldName, form);

  const params = useMemo(() => {
    const p: Record<string, unknown> = {
      search,
      fieldName: 'district',
      entityType: 'REPORT',
    };

    if (regionValue && regionValue !== '-1') {
      p.regionCode = regionValue;
    }

    return p;
  }, [search, regionValue]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('district', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select district'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load districts' : isLoading ? 'Loading...' : 'No districts'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default DistrictSelect;
